/**
 * Turns the legacy-bridge call log into the one line the header shows.
 *
 * During the transition the Delphi application is still the system of record
 * and replays every committed transaction into this app. The header indicator
 * is how an operator can see that happening at all: a call in progress is
 * announced while it runs, and its outcome stays on screen for five seconds
 * afterwards.
 *
 * Kept pure so the timing rule - the part that is easy to get subtly wrong -
 * is unit-testable without a clock or a rendered component.
 *
 * Reference: documentation/implementation_plan_legacy_delphi_bridge_api.md (section 7.7)
 */

/** How long a finished call stays visible in the header. */
export const BRIDGE_STATUS_VISIBLE_MS = 5000;

export type BridgeCallStatus = 'in_progress' | 'completed' | 'failed';

export type BridgeStatusSeverity = 'info' | 'success' | 'warning' | 'error';

export interface BridgeApiCall {
    callId: string;
    method: string;
    path: string;
    operations: string[];
    eventCount: number;
    cashPointId: number | null;
    workstation: string | null;
    status: string;
    httpStatus: number | null;
    durationMs: number | null;
    error: string | null;
    startedAt: string;
    finishedAt: string | null;
}

export interface BridgeActivityResponse {
    enabled: boolean;
    serverTime: string;
    calls: BridgeApiCall[];
}

export interface BridgeStatusMessage {
    callId: string;
    status: BridgeCallStatus;
    severity: BridgeStatusSeverity;
    /** Human-facing operation summary, e.g. `contract.create +2`. */
    operationLabel: string;
    operations: string[];
    eventCount: number;
    cashPointId: number | null;
    workstation: string | null;
    durationMs: number | null;
    httpStatus: number | null;
    error: string | null;
}

/** Milliseconds since the epoch, or null when the value is not a usable date. */
export const parseTimestamp = (value: string | null): number | null => {
    if (!value) {
        return null;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const normaliseStatus = (status: string): BridgeCallStatus => {
    if (status === 'in_progress' || status === 'failed') {
        return status;
    }
    return 'completed';
};

/**
 * A call that answered 4xx completed as an HTTP exchange but carries a business
 * rejection, which is worth distinguishing from a clean apply.
 */
const severityOf = (call: BridgeApiCall): BridgeStatusSeverity => {
    const status = normaliseStatus(call.status);
    if (status === 'in_progress') {
        return 'info';
    }
    if (status === 'failed') {
        return 'error';
    }
    return call.httpStatus !== null && call.httpStatus >= 400 ? 'warning' : 'success';
};

/** `contract.create`, `contract.create +2`, or empty when nothing is named. */
export const summariseOperations = (operations: string[]): string => {
    if (operations.length === 0) {
        return '';
    }
    if (operations.length === 1) {
        return operations[0];
    }
    return `${operations[0]} +${operations.length - 1}`;
};

export interface DatedBridgeCall {
    call: BridgeApiCall;
    startedAtMs: number;
}

/**
 * The newest call, by the same clock the visibility rule uses, carrying its
 * parsed start time so the caller never has to re-parse or guess.
 *
 * Calls arrive newest-first, but relying on the server's ordering would make
 * the header wrong rather than merely stale if that ever changed. A row whose
 * start time cannot be read is skipped: it could not be placed on the timeline,
 * and a message with no expiry would stay on screen forever.
 */
export const newestCall = (calls: BridgeApiCall[]): DatedBridgeCall | null => {
    let newest: DatedBridgeCall | null = null;

    for (const call of calls) {
        const startedAtMs = parseTimestamp(call.startedAt);
        if (startedAtMs === null) {
            continue;
        }
        if (!newest || startedAtMs > newest.startedAtMs) {
            newest = { call, startedAtMs };
        }
    }

    return newest;
};

/**
 * The newest call that has finished, whatever its outcome - the signal that the
 * figures the header shows may have just moved.
 *
 * A bridged transaction lands in the database during the call, so the moment a
 * call settles is the moment the register totals are worth re-reading. In
 * progress calls are ignored: nothing is committed yet, and re-reading then
 * would only show the pre-transaction figures again.
 *
 * The newest is picked by start time rather than by position, for the same
 * reason {@link newestCall} does: the server's ordering is not a guarantee the
 * header should depend on.
 */
export const latestSettledCallId = (calls: BridgeApiCall[]): string | null => {
    let newestId: string | null = null;
    let newestStartedAtMs = Number.NEGATIVE_INFINITY;

    for (const call of calls) {
        if (normaliseStatus(call.status) === 'in_progress') {
            continue;
        }
        const startedAtMs = parseTimestamp(call.startedAt);
        if (startedAtMs === null || startedAtMs <= newestStartedAtMs) {
            continue;
        }
        newestId = call.callId;
        newestStartedAtMs = startedAtMs;
    }

    return newestId;
};

/**
 * What the header should be showing right now, or null when it should be empty.
 *
 * A call still in progress is always shown - the operator is looking at the
 * moment it matters. A finished call is shown until
 * {@link BRIDGE_STATUS_VISIBLE_MS} has passed since it finished.
 */
export const selectHeaderStatus = (
    calls: BridgeApiCall[],
    nowMs: number,
): BridgeStatusMessage | null => {
    const newest = newestCall(calls);
    if (!newest) {
        return null;
    }

    const { call, startedAtMs } = newest;
    const status = normaliseStatus(call.status);

    if (status !== 'in_progress') {
        // When it finished, or failing that when it started - a finished call
        // with an unreadable end time still has to expire.
        const finishedAtMs = parseTimestamp(call.finishedAt) ?? startedAtMs;
        if (nowMs - finishedAtMs >= BRIDGE_STATUS_VISIBLE_MS) {
            return null;
        }
    }

    return {
        callId: call.callId,
        status,
        severity: severityOf(call),
        operationLabel: summariseOperations(call.operations),
        operations: call.operations,
        eventCount: call.eventCount,
        cashPointId: call.cashPointId,
        workstation: call.workstation,
        durationMs: call.durationMs,
        httpStatus: call.httpStatus,
        error: call.error,
    };
};
